import { useCallback } from 'react';
import { Stack, Group, Center, Button, Badge, CSSObject } from '@mantine/core';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';
import { textVariants } from '@ui/tokens/typography/typography';
import useCurrentUserProjectPermissions from '@projectMembers/hooks/useCurrentUserProjectPermissions';
import useRequestUpgradeRoleMutation from '@projects/queries/useRequestUpgradeRoleMutation';
import { trackManagerPermissionsUpgradeRequested } from 'src/services/tracker/trackers/projects';
import { UPGRADE_REQUEST_SOURCES } from 'src/new_arch/modules/projectView/components/OverviewPermissionTooltip/upgradeRequestSources';
import GuestHash from '@projects/models/guestHash';

import Project from '../../../../../entities/project';

import styles from './ShareInviteLink.module.scss';

import InviteLinks from './InviteLinks/InviteLinks';

export type ShareInviteLinkProps = {
	project : Project;
	onInviteLinkChange?: ( guestHash: GuestHash ) => void;
};

const BADGE_TEXT_STYLES = {
	inner: {
		color: '#1C1D24',
		...( textVariants[ 'caption-semibold' ] as CSSObject )
	}
};

const ShareInviteLink = ( { project, onInviteLinkChange }: ShareInviteLinkProps ) => {
	const permissions = useCurrentUserProjectPermissions( project.id );
	const currentUserCanShareInviteLink = permissions.currentUserCan( 'shareInviteLink', 'projects' );
	const { mutate: requestUpgradeRole } = useRequestUpgradeRoleMutation();

	const onRequestPermissionClick = useCallback( () => {
		trackManagerPermissionsUpgradeRequested( {
			projectID: project.id, origin: UPGRADE_REQUEST_SOURCES.PUBLIC_LINK_SHARING
		} );
		requestUpgradeRole( { projectID: project.id, source: UPGRADE_REQUEST_SOURCES.PUBLIC_LINK_SHARING } );
	}, [ requestUpgradeRole, project.id ] );

	return (
		<Stack className={styles.ShareInviteLink} spacing={12}>
			<Stack spacing={4}>
				<Group align="center" spacing={6}>
					<Center bg="blue.1" p={4} style={{ borderRadius: 4 }}>
						<Icon icon="public" size={14} color="blue.5" />
					</Center>
					<Text variant="footnote-semibold" color="var(--color-text-primary)">Share link invite</Text>
					<Badge color="blue" size="xs" styles={BADGE_TEXT_STYLES}>No Account Required</Badge>
				</Group>
				<Text variant="footnote" color="var(--color-text-secondary)">
					Anyone can access if the link is open and assume the invite role.
				</Text>
			</Stack>

			{currentUserCanShareInviteLink ? (
				<InviteLinks
					project={project}
					onInviteLinkChange={onInviteLinkChange}
				/>
			) : (
				<Group spacing={8} position="apart" bg="var(--color-primary-subtle)" p={12} style={{ borderRadius: 4 }}>
					<Text variant="footnote" color="var(--color-text-primary)">
						Only managers can enable and send share links
					</Text>
					<Button h={19} variant="subtle" color="primary.5" size="xs" w="fit-content" compact p={0} onClick={onRequestPermissionClick}>
						Request permission
					</Button>
				</Group>
			)}
		</Stack>
	);
};

export default ShareInviteLink;
